import dayjs from 'dayjs'
import type { ISpProfilePublic } from '@brushfire/types'
import type { IconType } from 'react-icons'
import { accountDemographicIcons } from '@/features/account/lib/account-demographic-icons'
import { formatUsNanpDisplay } from '@/features/account/lib/account-phone-utils'
import {
  CHILDREN_OPTIONS,
  EDUCATION_OPTIONS,
  EMPLOYMENT_OPTIONS,
  GENDER_OPTIONS,
  HOUSING_OPTIONS,
  HOUSEHOLD_OPTIONS,
  INDUSTRY_OPTIONS,
  MARITAL_OPTIONS,
  US_STATE_OPTIONS,
  type CatalogOption,
} from '@/features/rewards/lib/demographicCatalog'
import { getCitiesForState, resolveStateCode } from '@/features/rewards/lib/usLocationCatalog'

export type AccountFieldKey =
  | 'firstName'
  | 'lastName'
  | 'email'
  | 'phone'
  | 'gender'
  | 'maritalStatus'
  | 'dateOfBirth'
  | 'householdSize'
  | 'city'
  | 'employment'
  | 'numberOfChildren'
  | 'industry'
  | 'education'
  | 'housingStatus'
  | 'state'

export interface AccountFieldRow {
  key: AccountFieldKey
  label: string
  icon: IconType
}

function labelFor(options: CatalogOption[], value: string | null | undefined): string | null {
  if (!value) return null
  return options.find((o) => o.value === value)?.label ?? value
}

function cityLabel(state: string | undefined, city: string | null | undefined): string | null {
  if (!state || !city) return null
  const cities = getCitiesForState(state)
  return cities.find((c) => c.value === city)?.label ?? city
}

export function formatProfileState(profile: ISpProfilePublic): string | null {
  const stateCode = resolveStateCode(profile.locationData?.state)
  return labelFor(US_STATE_OPTIONS, stateCode || null)
}

export function formatLocation(profile: ISpProfilePublic): string | null {
  const state = profile.locationData?.state
  const city = profile.locationData?.city
  if (!state && !city) return null
  const stateLabel = labelFor(US_STATE_OPTIONS, state ?? null)
  const cityLbl = cityLabel(state, city)
  if (stateLabel && cityLbl) return `${cityLbl}, ${stateLabel}`
  return stateLabel ?? cityLbl
}

export function formatDob(iso: string | null | undefined): string | null {
  if (!iso) return null
  const datePart = iso.includes('T') ? iso.slice(0, 10) : iso
  const parsed = dayjs(datePart)
  if (!parsed.isValid()) return null
  return parsed.format('MM/DD/YYYY')
}

export function formatDobForView(profile: ISpProfilePublic): string | null {
  return formatDob(profile.dateOfBirth)
}

export function displayValueForField(
  profile: ISpProfilePublic,
  key: AccountFieldKey,
): string | null {
  switch (key) {
    case 'firstName':
      return profile.firstName || null
    case 'lastName':
      return profile.lastName || null
    case 'email':
      return profile.email || null
    case 'phone':
      return profile.phone ? formatUsNanpDisplay(profile.phone) : null
    case 'gender':
      return labelFor(GENDER_OPTIONS, profile.gender)
    case 'maritalStatus':
      return labelFor(MARITAL_OPTIONS, profile.maritalStatus)
    case 'dateOfBirth':
      return formatDobForView(profile)
    case 'householdSize':
      return labelFor(HOUSEHOLD_OPTIONS, profile.householdSize)
    case 'city':
      return cityLabel(profile.locationData?.state, profile.locationData?.city)
    case 'state':
      return labelFor(US_STATE_OPTIONS, profile.locationData?.state)
    case 'employment':
      return labelFor(EMPLOYMENT_OPTIONS, profile.employment)
    case 'numberOfChildren':
      return labelFor(CHILDREN_OPTIONS, profile.numberOfChildren)
    case 'industry':
      return labelFor(INDUSTRY_OPTIONS, profile.industry)
    case 'education':
      return labelFor(EDUCATION_OPTIONS, profile.education)
    case 'housingStatus':
      return labelFor(HOUSING_OPTIONS, profile.housingStatus)
    default:
      return null
  }
}

/** Figma 5724-11865 — paired demographic grid rows */
export const ACCOUNT_DEMOGRAPHIC_GRID_ROWS: [AccountFieldRow, AccountFieldRow][] = [
  [
    { key: 'gender', label: 'Gender', icon: accountDemographicIcons.gender },
    { key: 'maritalStatus', label: 'Marital Status', icon: accountDemographicIcons.maritalStatus },
  ],
  [
    { key: 'dateOfBirth', label: 'Date of Birth', icon: accountDemographicIcons.dateOfBirth },
    { key: 'householdSize', label: 'House Hold Size', icon: accountDemographicIcons.householdSize },
  ],
  [
    { key: 'city', label: 'City', icon: accountDemographicIcons.city },
    { key: 'employment', label: 'Employment Status', icon: accountDemographicIcons.employment },
  ],
  [
    { key: 'numberOfChildren', label: 'No of Children', icon: accountDemographicIcons.numberOfChildren },
    { key: 'industry', label: 'Industry/Field Working', icon: accountDemographicIcons.industry },
  ],
  [
    { key: 'education', label: 'Education Level', icon: accountDemographicIcons.education },
    { key: 'housingStatus', label: 'Housing Status', icon: accountDemographicIcons.housingStatus },
  ],
]
